/**
 * § G28 — the printable flight-options sheet, focus flight FIRST.
 *
 * ── Why paper at all ───────────────────────────────────────────────────────
 * An organizer sorting eight people's travel is often talking to somebody who
 * is not in the app: a boss approving spend, an assistant booking it, a
 * traveller who wants the choices in front of them on a call. A URL behind a
 * login does not survive that conversation; a page does.
 *
 * ── The one rule this page exists to honour ────────────────────────────────
 * **The focus flight goes at the top, and is labelled as the recommendation.**
 * A print-out that opens with a cheapest-first list invites the reader to book
 * the cheapest row, which is exactly the decision the focus set exists to
 * improve — it already weighed arrival against the meeting, airline preference
 * and price. Burying it in a sorted list would throw that judgement away at the
 * precise moment somebody acts on the page.
 *
 * ── Print mechanics ────────────────────────────────────────────────────────
 * Follows `ItineraryPrint`: white background, no app chrome, `window.print()`
 * once the data has painted, and the browser's own "Save as PDF" as the PDF
 * engine. No PDF library is pulled in — a dependency that renders a second,
 * subtly different layout is how the printed sheet starts disagreeing with the
 * screen.
 */
import { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plane, Printer, ArrowLeft, ArrowRight, Star } from 'lucide-react';
import { travelGroupApi, type FlightOptionsPrintData } from '@/lib/api';

/** "$126" or null. NEVER "$0" for an unknown price — see § T. */
function money(n: number | null | undefined): string | null {
  if (n === null || n === undefined || !Number.isFinite(n)) return null;
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

function clock(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function stopsLabel(stops: number | null | undefined): string {
  if (stops === null || stops === undefined) return '';
  if (stops === 0) return 'Nonstop';
  return `${stops} stop${stops === 1 ? '' : 's'}`;
}

function OptionRow({ f, featured }: { f: any; featured?: boolean }) {
  const dep = clock(f?.departAt ?? f?.departureTime ?? null);
  const arr = clock(f?.arriveAt ?? f?.arrivalTime ?? null);
  const price = money(f?.price ?? f?.priceUsd ?? null);
  return (
    <div
      className={
        featured
          ? 'flex flex-col gap-1 rounded-md border-2 border-gray-900 bg-gray-50 p-3'
          : 'flex flex-col gap-1 border-b border-gray-200 py-2 last:border-0'
      }
      style={{ breakInside: 'avoid' }}
    >
      <div className="flex items-center gap-2 text-sm">
        {featured && <Star className="h-3.5 w-3.5 fill-gray-900 text-gray-900" aria-hidden="true" />}
        <Plane className="h-3.5 w-3.5 -rotate-45 text-gray-500" aria-hidden="true" />
        <span className="font-semibold">{f?.airline || 'Flight'}</span>
        {f?.flightNumber && <span className="font-mono text-xs text-gray-500">{f.flightNumber}</span>}
        {price && <span className="ml-auto font-mono text-base font-bold">{price}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-x-2 pl-5 text-xs text-gray-600">
        {dep && <span className="font-mono">{dep}</span>}
        <ArrowRight className="h-3 w-3 text-gray-400" aria-hidden="true" />
        {arr && <span className="font-mono">{arr}</span>}
        <span>{stopsLabel(f?.stops)}</span>
        {f?.durationLabel && <span className="font-mono">{f.durationLabel}</span>}
      </div>
    </div>
  );
}

export default function FlightOptionsPrint() {
  const { groupId, membershipId } = useParams<{ groupId: string; membershipId: string }>();
  const printed = useRef(false);

  const { data, isLoading, isError, error } = useQuery<FlightOptionsPrintData>({
    queryKey: ['flight-options-print', groupId, membershipId],
    queryFn: () => travelGroupApi.flightOptionsForPrint(groupId!, membershipId!),
    enabled: Boolean(groupId && membershipId),
  });

  useEffect(() => {
    if (!data || printed.current) return;
    // Let the DOM paint before the print dialog. The guard lives INSIDE the
    // callback so StrictMode's mount->cleanup->remount still re-schedules,
    // while production fires exactly once. (Same shape as ItineraryPrint.)
    const t = setTimeout(() => {
      if (printed.current) return;
      printed.current = true;
      window.print();
    }, 400);
    return () => clearTimeout(t);
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white text-gray-500">
        Preparing the options sheet…
      </div>
    );
  }

  if (isError || !data) {
    // The server refuses with a REASON — no trip, nothing shopped yet, not the
    // organizer. Echoing it beats a generic failure the reader cannot act on.
    const msg = (error as any)?.response?.data?.error || 'Those options could not be read.';
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-white text-center">
        <p className="text-gray-700">{msg}</p>
        <Link to={`/events/${groupId}`} className="text-sm text-gray-500 underline">
          Back to the meeting
        </Link>
      </div>
    );
  }

  const legs = data.trip_view?.legs ?? [];

  return (
    <div className="min-h-screen bg-white p-8 text-gray-900">
      {/* Screen-only controls. `print:hidden` keeps them off the paper. */}
      <div className="mb-6 flex items-center gap-3 print:hidden">
        <Link to={`/events/${groupId}`} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" /> Back to the meeting
        </Link>
        <button
          onClick={() => window.print()}
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
        >
          <Printer className="h-4 w-4" /> Print / Save as PDF
        </button>
      </div>

      <header className="mb-6 border-b border-gray-300 pb-4">
        <h1 className="text-2xl font-bold">{data.attendee_name} — flight options</h1>
        <p className="text-sm text-gray-600">{data.event_name}</p>
        {data.when_line && <p className="text-sm text-gray-600">{data.when_line}</p>}
      </header>

      {legs.map((leg: any, i: number) => {
        // Never show the recommendation twice: the options list is filtered
        // against the focus flight's id rather than trusting the server to have
        // excluded it.
        const focus = leg.focus;
        const rest = (leg.options ?? []).filter((o: any) => !focus || o?.id !== focus?.id);
        return (
          <section key={i} className="mb-6" style={{ breakInside: 'avoid' }}>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              {leg.role === 'return' ? 'Return' : 'Outbound'} · {leg.origin} → {leg.destination}
              {leg.dateLabel ? ` · ${leg.dateLabel}` : ''}
            </h2>

            {focus ? (
              <>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-900">
                  Recommended
                </p>
                <OptionRow f={focus} featured />
              </>
            ) : (
              // Stated, not blank. "No pick yet" is a different fact from "no
              // flights", and an organizer reading a silent gap cannot tell.
              <p className="text-xs text-gray-500">No recommended flight picked yet for this leg.</p>
            )}

            {rest.length > 0 && (
              <div className="mt-3">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Other options
                </p>
                {rest.map((o: any, j: number) => (
                  <OptionRow key={j} f={o} />
                ))}
              </div>
            )}
          </section>
        );
      })}

      <footer className="mt-8 border-t border-gray-300 pt-3 text-xs text-gray-500">
        Fares are what we last saw and change without notice — confirm at the airline before booking.
      </footer>
    </div>
  );
}
